import React, { Suspense, lazy } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { ThemeProvider } from './contexts/ThemeContext';
import { Toaster } from './components/ui/sonner';
import ErrorBoundary from './components/ErrorBoundary';
import LoginPage from './pages/LoginPage';
import SSOCallbackPage from './pages/SSOCallbackPage';
import Layout from './components/Layout';
import MiraFeedbackWidget from './components/MiraFeedbackWidget';
import { MiraProvider } from './contexts/MiraContext';
// Side-effect import: installs the console-capture patch the moment
// the app boots so Mira can replay errors that happened BEFORE the
// user opens the widget.
import './lib/mira-telemetry';
import './App.css';

// Lazy-loaded page components for code splitting
const Dashboard = lazy(() => import('./pages/Dashboard'));
const ContractsPage = lazy(() => import('./pages/ContractsPage'));
const ProjectsPage = lazy(() => import('./pages/ProjectsPage'));
const TasksPage = lazy(() => import('./pages/TasksPage'));
const WorkforcePage = lazy(() => import('./pages/WorkforcePage'));
const WorkloadPage = lazy(() => import('./pages/WorkloadPage'));
const EmployeeCapacityPage = lazy(() => import('./pages/EmployeeCapacityPage'));
const AdminPage = lazy(() => import('./pages/AdminPage'));
const FeedbackWikiPage = lazy(() => import('./pages/FeedbackWikiPage'));

const PrivateRoute = ({ children }: { children: React.ReactNode }) => {
  const { isAuthenticated, loading } = useAuth();
  
  if (loading) {
    return <div className="min-h-screen flex items-center justify-center">Loading...</div>;
  }
  
  return isAuthenticated ? children : <Navigate to="/login" />;
};

const PageLoader = () => (
  <div className="min-h-screen flex items-center justify-center">
    <div className="text-center">
      <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-slate-600 mx-auto mb-2"></div>
      <p className="text-sm text-slate-500">Loading...</p>
    </div>
  </div>
);

function AppRoutes() {
  return (
    <Suspense fallback={<PageLoader />}>
      <Routes>
      <Route path="/login" element={<LoginPage />} />
      <Route path="/sso-callback" element={<SSOCallbackPage />} />
      <Route path="/dashboard" element={<Navigate to="/" replace />} />
      <Route path="/" element={
        <PrivateRoute>
          <Layout>
            <ErrorBoundary name="Dashboard" fallbackMessage="The dashboard encountered an error. Your data is safe.">
              <Dashboard />
            </ErrorBoundary>
          </Layout>
        </PrivateRoute>
      } />
      <Route path="/contracts" element={
        <PrivateRoute>
          <Layout>
            <ErrorBoundary name="Contracts" fallbackMessage="The contracts page encountered an error. Your data is safe.">
              <ContractsPage />
            </ErrorBoundary>
          </Layout>
        </PrivateRoute>
      } />
      <Route path="/projects" element={
        <PrivateRoute>
          <Layout>
            <ErrorBoundary name="Projects" fallbackMessage="The projects page encountered an error. Your data is safe.">
              <ProjectsPage />
            </ErrorBoundary>
          </Layout>
        </PrivateRoute>
      } />
      <Route path="/tasks" element={
        <PrivateRoute>
          <Layout>
            <ErrorBoundary name="Tasks" fallbackMessage="The tasks page encountered an error. Your data is safe.">
              <TasksPage />
            </ErrorBoundary>
          </Layout>
        </PrivateRoute>
      } />
      <Route path="/workforce" element={
        <PrivateRoute>
          <Layout>
            <ErrorBoundary name="Workforce" fallbackMessage="The workforce page encountered an error. Your data is safe.">
              <WorkforcePage />
            </ErrorBoundary>
          </Layout>
        </PrivateRoute>
      } />
      <Route path="/workload" element={
        <PrivateRoute>
          <Layout>
            <ErrorBoundary name="Workload" fallbackMessage="The workload page encountered an error. Your data is safe.">
              <WorkloadPage />
            </ErrorBoundary>
          </Layout>
        </PrivateRoute>
      } />
      <Route path="/employees/:employeeId/capacity" element={
        <PrivateRoute>
          <Layout>
            <ErrorBoundary name="EmployeeCapacity" fallbackMessage="The capacity page encountered an error. Your data is safe.">
              <EmployeeCapacityPage />
            </ErrorBoundary>
          </Layout>
        </PrivateRoute>
      } />
      <Route path="/employees/capacity" element={
        <PrivateRoute>
          <Layout>
            <ErrorBoundary name="EmployeeCapacity" fallbackMessage="The capacity page encountered an error. Your data is safe.">
              <EmployeeCapacityPage />
            </ErrorBoundary>
          </Layout>
        </PrivateRoute>
      } />
      <Route path="/admin" element={
        <PrivateRoute>
          <Layout>
            <ErrorBoundary name="Admin" fallbackMessage="The admin page encountered an error. Your data is safe.">
              <AdminPage />
            </ErrorBoundary>
          </Layout>
        </PrivateRoute>
      } />
      <Route path="/feedback/issue/:sessionId" element={
        <PrivateRoute>
          <Layout>
            <ErrorBoundary name="FeedbackWiki" fallbackMessage="The feedback wiki encountered an error.">
              <FeedbackWikiPage />
            </ErrorBoundary>
          </Layout>
        </PrivateRoute>
      } />
    </Routes>
    </Suspense>
  );
}

function App() {
  return (
    <ThemeProvider>
      <AuthProvider>
        <MiraProvider>
          <BrowserRouter>
            <AppRoutes />
            <MiraFeedbackWidget />
            <Toaster position="top-right" richColors closeButton />
          </BrowserRouter>
        </MiraProvider>
      </AuthProvider>
    </ThemeProvider>
  );
}

export default App;
